import { getImageType } from '../../helpers/image';
import { generateUuid } from '../../helpers/string';
import { IMAGE_PLACEHOLDER } from '../../mock/mock-data';
import { MeilisearchImage, MeilisearchProductResource } from '../@api/Meilisearch';
import { defaultSyliusImageType, ProductImage, SyliusImageType } from '../@api/Sylius';
import { ImageDeviceTypeResource, ImageWithFormatsResource } from '../@blocks/ProductCollection/ProductCollection';
import {
    AutoplayMediaOption,
    Image,
    ImageFormat,
    ImageFormatSize,
    MediaFormatResource,
    MediaItem,
    MediaItemResource,
    MediaResource,
    Video,
    VideoDeviceTypeResource,
    VideoFormat,
    VideoFormatSize,
    VideoResource,
} from './Media';

const transformToImageFormat = (resource: MediaFormatResource, format: ImageFormatSize): ImageFormat => {
    const mediaSizes = {
        [ImageFormatSize.original]: '(min-width: 1024px)',
        [ImageFormatSize.large]: '(min-width: 768px)',
        [ImageFormatSize.medium]: '(min-width: 480px)',
        [ImageFormatSize.small]: '(min-width: 320px)',
        [ImageFormatSize.thumbnail]: '(max-width: 320px)',
    };

    return {
        srcSet: resource.url,
        type: resource.mime,
        media: mediaSizes[format],
    };
};

export const transformToVideoFormat = (resource: MediaResource, format: VideoFormatSize): VideoFormat => {
    const mediaSizes = {
        [VideoFormatSize.large]: '(min-width: 768px)',
        [VideoFormatSize.small]: '(max-width: 768px)',
    };

    return {
        src: resource.url,
        type: resource.mime,
        media: mediaSizes[format],
    };
};

export const transformToImageFormats = (
    resource: Partial<Record<ImageFormatSize, MediaFormatResource>>,
): Partial<Record<ImageFormatSize, ImageFormat>> => {
    const original = resource.original
        ? transformToImageFormat(resource.original, ImageFormatSize.original)
        : undefined;

    const large = resource.large
        ? transformToImageFormat(resource.large, ImageFormatSize.large)
        : undefined;

    const medium = resource.medium
        ? transformToImageFormat(resource.medium, ImageFormatSize.medium)
        : undefined;

    const small = resource.small
        ? transformToImageFormat(resource.small, ImageFormatSize.small)
        : undefined;

    const thumbnail = resource.thumbnail
        ? transformToImageFormat(resource.thumbnail, ImageFormatSize.thumbnail)
        : undefined;

    return {
        original,
        large,
        medium,
        small,
        thumbnail,
    };
};

export const transformToVideoFormats = (
    resource: Partial<Record<VideoFormatSize, MediaResource>>,
): Partial<Record<VideoFormatSize, VideoFormat>> => {
    const large = resource.large
        ? transformToVideoFormat(resource.large, VideoFormatSize.large)
        : undefined;

    const small = resource.small
        ? transformToVideoFormat(resource.small, VideoFormatSize.small)
        : undefined;

    return {
        large,
        small,
    };
};

export const transformToImage = (resource: MediaResource): Image => {
    const formats = resource?.formats
        ? transformToImageFormats({
            ...resource.formats,
            [ImageFormatSize.original]: resource,
        })
        : {};

    return {
        src: resource?.url || '',
        formats,
        alt: resource?.alternativeText || resource?.caption || resource?.name || '',
    };
};

export const transformToImageWithFormats = (resources: ImageWithFormatsResource[]): Image => {
    const mobileFormat = resources.find(image => image.size === ImageDeviceTypeResource.mobile);
    const tabletPortraitFormat = resources.find(image => image.size === ImageDeviceTypeResource.tablet);
    const tabletLandscapeFormat = resources.find(image => image.size === ImageDeviceTypeResource.laptop);
    const desktopFormat = resources.find(image => image.size === ImageDeviceTypeResource.desktop);

    const formats = transformToImageFormats({
        [ImageFormatSize.small]: mobileFormat?.image,
        [ImageFormatSize.medium]: tabletPortraitFormat?.image,
        [ImageFormatSize.large]: tabletLandscapeFormat?.image,
        [ImageFormatSize.original]: desktopFormat?.image,
    });

    const defaultImage = mobileFormat?.image
        || tabletPortraitFormat?.image
        || tabletLandscapeFormat?.image
        || desktopFormat?.image
        || resources[0].image;

    return {
        src: defaultImage?.url || '',
        formats,
        alt: defaultImage?.alternativeText || defaultImage?.caption || defaultImage?.name || '',
    };
};

export const transformMeilisearchImageToImage = (image: MeilisearchImage): Image => ({
    src: image.url,
    formats: {
        original: {
            srcSet: image.url,
            type: getImageType(image.url),
            media: '(min-width: 320px)',
        },
    },
    alt: image.alt,
});

export const transformSyliusImageFormat = (url: string, from: SyliusImageType, to: SyliusImageType): string => url
    .replace('http://', 'https://')
    .replace(from, to);

export const transformToMediumProductImages = (image: MeilisearchImage): MeilisearchImage => ({
    ...image,
    url: transformSyliusImageFormat(image.url, SyliusImageType.original, SyliusImageType.medium),
});

export const transformMeilisearchImagesToMediaItems = (resource: MeilisearchProductResource): MediaItem[] => {
    const mediaItems: MeilisearchImage[] = [];

    if (resource.main_image) {
        mediaItems.push(transformToMediumProductImages(resource.main_image));
    }

    if (resource.image && resource.image.url !== resource?.main_image?.url) {
        mediaItems.push(transformToMediumProductImages(resource.image));
    }

    return mediaItems.map((image, index) => ({
        id: index.toString(),
        image: transformMeilisearchImageToImage(image),
    }));
};

export const defaultImage = (): Image => transformMeilisearchImageToImage(IMAGE_PLACEHOLDER);

export const defaultMediaItem = (): MediaItem => ({
    id: generateUuid(),
    image: defaultImage(),
});

export const transformImagePathToImage = (imagePath: string, altText: string, imageOverwriteType = defaultSyliusImageType): Image => {
    // TODO L5T-156: Remove this conversion, once the website is live, but check if all images have been resolved first
    const imagePathEnd = imagePath.split(imageOverwriteType)[1];
    const largeImagePath = SyliusImageType.loods5square2000 + imagePathEnd;

    const resolveImageSrcSet = `${process.env.REACT_APP_SYLIUS_MEDIA_RESOLVE_URL}/${largeImagePath}`;

    return {
        src: imagePath,
        formats: {
            original: {
                srcSet: resolveImageSrcSet,
                type: getImageType(imagePath),
                media: '(min-width: 1024px)',
            },
            thumbnail: {
                srcSet: imagePath,
                type: getImageType(imagePath),
                media: '(max-width: 1024px)',
            },
        },
        alt: altText,
    };
};

export const transformProductImageToMediaItem = (resource: ProductImage, productName: string): MediaItem => {
    const id = resource.id
        ? resource.id.toString()
        : generateUuid();

    const image = transformImagePathToImage(resource.path, productName);

    return { id, image };
};

export const transformToVideo = (resource: MediaResource, poster?: Image): Video => ({
    src: resource?.url || '',
    type: resource?.mime || '',
    poster,
});

export const transformToVideoWithFormats = (resources: VideoResource[]): Video => {
    const type = resources[0].video.mime;
    const poster = transformToImage(resources[0].poster);

    const mobileFormat = resources.find(video => video.deviceType === VideoDeviceTypeResource.mobile);
    const desktopFormat = resources.find(video => video.deviceType === VideoDeviceTypeResource.desktop);

    const formats = transformToVideoFormats({
        [VideoFormatSize.small]: mobileFormat?.video,
        [VideoFormatSize.large]: desktopFormat?.video,
    });

    return {
        type,
        poster,
        formats,
    };
};

export const transformToMediaItem = (resource: MediaItemResource): MediaItem => {
    const id = resource.id
        ? resource.id.toString()
        : generateUuid();

    const { media } = resource;

    const image = media?.mime.startsWith('image')
        ? transformToImage(media)
        : undefined;

    const video = media?.mime.startsWith('video')
        ? transformToVideo(media)
        : undefined;

    const videoPlaysInline = !!video && resource.playOption === AutoplayMediaOption.autoPlay;

    return {
        id,
        image,
        video,
        videoPlaysInline,
    };
};
